import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TIME_ZONE } from '../components/common/constants/common';
import { PlaceStatus, DAYS_OF_WEEK, CLOSING_SOON_MINUTES } from '../components/common/constants';

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Checks if dayName (e.g. "Wednesday") falls within a schedule string from the API.
// Schedule strings can be: "Everyday", "To Be Determined", a single day ("Monday"),
// a range ("Monday - Friday"), or a comma/and-separated list ("Monday, Wednesday and Friday").
// Ranges that wrap around the week (e.g. "Friday - Monday") are supported.
function matchesDay(dayString, dayName) {
  if (dayString === 'Everyday') return true;
  if (dayString === 'To Be Determined') return false;

  const parts = dayString.includes(' and ') ? dayString.split(/, | and /) : [dayString];

  return parts.some((part) => {
    if (part.includes(' - ')) {
      const [startDay, endDay] = part.split(' - ');
      const targetIndex = DAYS_OF_WEEK.indexOf(dayName);
      const startIndex = DAYS_OF_WEEK.indexOf(startDay);
      const endIndex = DAYS_OF_WEEK.indexOf(endDay);

      if (targetIndex === -1 || startIndex === -1 || endIndex === -1) return false;

      // Standard range (e.g. Mon-Fri): target must be between start and end
      if (startIndex <= endIndex) {
        return targetIndex >= startIndex && targetIndex <= endIndex;
      }
      // Wrap-around range (e.g. Fri-Mon): target is before end OR after start
      return targetIndex >= startIndex || targetIndex <= endIndex;
    }
    return part === dayName;
  });
}

// Parses a single time range string (e.g. "8:00 AM - 5:00 PM") into dayjs objects.
// Normalizes "Midnight" to "12:00 AM" for parsing but preserves it as the display label.
// Returns { start, end, endTimeLabel } or null if the string has no end time.
function parseTimeRange(timeString) {
  const now = dayjs().tz(TIME_ZONE);
  const today = now.format('YYYY-MM-DD');

  let [startTime, endTime] = timeString.split(' - ');
  if (!endTime) return null;

  if (endTime === 'Midnight') endTime = '12:00 AM';

  const start = dayjs.tz(`${today} ${startTime}`, 'YYYY-MM-DD hh:mm A', TIME_ZONE);
  const end = dayjs.tz(`${today} ${endTime}`, 'YYYY-MM-DD hh:mm A', TIME_ZONE);

  return { start, end, endTimeLabel: endTime === '12:00 AM' ? 'Midnight' : endTime };
}

// Checks if the current time falls within any time range in the string.
// Supports multiple ranges joined by " and " (e.g. "8:00 AM - 12:00 PM and 1:00 PM - 5:00 PM").
// Handles overnight ranges where end is before start (e.g. "10:00 PM - 2:00 AM").
// Returns the matching parsed range, or null if not currently within any range.
function isWithinTimeRange(timeString) {
  const now = dayjs().tz(TIME_ZONE);
  const ranges = timeString.includes(' and ') ? timeString.split(' and ') : [timeString];

  for (const range of ranges) {
    const parsed = parseTimeRange(range);
    if (!parsed) continue;

    const { start, end } = parsed;

    if (end.isBefore(start)) {
      // Overnight range: now is valid if it's after start OR before end
      if (now.isAfter(start) || now.isBefore(end)) return parsed;
    } else {
      if (now.isBetween(start, end, null, '[]')) return parsed;
    }
  }
  return null;
}

// Extracts the opening time from a times string (e.g. "8:00 AM - 5:00 PM" returns "8:00 AM").
// Returns null for "Closed" or "24 Hours" entries since they don't have a meaningful start time.
// When multiple ranges exist, uses the first one as the opening time.
function getOpeningTime(timesString) {
  const normalized = timesString.toLowerCase();
  if (normalized === 'closed' || normalized === '24 hours') return null;

  const ranges = timesString.includes(' and ') ? timesString.split(' and ') : [timesString];
  const firstRange = ranges[0];
  const [startTime] = firstRange.split(' - ');
  return startTime ? startTime.trim() : null;
}

// Determines the current operating status of a place based on its hours schedule.
// All times are evaluated in the university's timezone (America/New_York).
//
// Each entry in the hours array has:
//   - title: the day(s) it applies to (e.g. "Monday - Friday", "Everyday", "Saturday")
//   - times: the operating hours (e.g. "8:00 AM - 5:00 PM", "24 Hours", "Closed")
//
// Returns { status, closesAt, opensTomorrowAt } where:
//   - status: PlaceStatus.OPEN | CLOSING_SOON | CLOSED
//   - closesAt: display string for when it closes (e.g. "5:00 PM"), null if not applicable
//   - opensTomorrowAt: display string for tomorrow's opening time, null if not applicable
export function getPlaceStatus(hours) {
  const result = { status: PlaceStatus.CLOSED, closesAt: null, opensTomorrowAt: null };

  if (!Array.isArray(hours) || hours.length === 0) return result;

  const now = dayjs().tz(TIME_ZONE);
  const currentDay = now.format('dddd');

  // First pass: check if the place is currently open by finding a matching day + active time range
  for (const entry of hours) {
    if (!entry.title || !entry.times) continue;

    const timesLower = entry.times.toLowerCase();
    if (timesLower === 'closed') continue;

    if (!matchesDay(entry.title, currentDay)) continue;

    if (timesLower === '24 hours') {
      return { status: PlaceStatus.OPEN, closesAt: null, opensTomorrowAt: null };
    }

    const activeRange = isWithinTimeRange(entry.times);
    if (activeRange) {
      const { end, endTimeLabel } = activeRange;

      // For overnight ranges, shift end to next day to calculate minutes correctly
      let effectiveEnd = end;
      if (end.isBefore(activeRange.start)) {
        effectiveEnd = end.add(1, 'day');
      }

      const minutesUntilClose = effectiveEnd.diff(now, 'minute');

      return {
        status:
          minutesUntilClose <= CLOSING_SOON_MINUTES ? PlaceStatus.CLOSING_SOON : PlaceStatus.OPEN,
        closesAt: endTimeLabel,
        opensTomorrowAt: null,
      };
    }
  }

  // Second pass: place is closed — find tomorrow's opening time to display "Opens tomorrow at X"
  const tomorrowDay = DAYS_OF_WEEK[(DAYS_OF_WEEK.indexOf(currentDay) + 1) % 7];

  for (const entry of hours) {
    if (!entry.title || !entry.times) continue;

    const timesLower = entry.times.toLowerCase();
    if (timesLower === 'closed') continue;

    if (matchesDay(entry.title, tomorrowDay)) {
      if (timesLower === '24 hours') {
        result.opensTomorrowAt = '12:00 AM';
        break;
      }
      const openTime = getOpeningTime(entry.times);
      if (openTime) {
        result.opensTomorrowAt = openTime;
        break;
      }
    }
  }

  return result;
}
